import React from "react";

const EMPHASIS_TONE_MAP = [
  { re: /^(specific|spécifique|específico|spezifisch)/i, tone: "s" },
  { re: /^(measurable|mesurable|medible|messbar)/i, tone: "m" },
  { re: /^(achievable|atteignable|alcanzable|erreichbar)/i, tone: "a" },
  { re: /^(relevant|réaliste|relevante)/i, tone: "r" },
  { re: /^(time-?bound|temporel|temporal|terminiert)/i, tone: "t" },
  { re: /^smart$/i, tone: "smart" },
  { re: /^(competition|compétition|competición|wettkampf)/i, tone: "goal" },
  { re: /^(recreational|loisir|recreativo|freizeit)/i, tone: "goal" },
  { re: /^(health|santé|salud|gesundheit)/i, tone: "goal" },
  { re: /^(analyse|analysis|impact|principal|primary|option|sommaire|summary)/i, tone: "default" },
];

function emphasisTone(word) {
  const hit = EMPHASIS_TONE_MAP.find(({ re }) =>
    re.test(String(word || "").trim())
  );
  return hit ? hit.tone : "default";
}

/**
 * Strip Gemini definition-list colons and heading artifacts so chat copy
 * reads as normal prose instead of ": section" lines.
 */
export function normalizeDigipingText(raw) {
  let t = String(raw || "").replace(/\r\n/g, "\n");
  t = t.replace(/<!--[\s\S]*?-->/g, "");
  t = t.replace(/^\s{0,3}#{1,6}\s+/gm, "");
  t = t.replace(/^\s*([-*_]){3,}\s*$/gm, "");
  t = t.replace(/^[ \t]*:+[ \t]*$/gm, "");
  t = t.replace(/^[ \t]*:+[ \t]+/gm, "");
  t = t.replace(/^(\*\*[^*]+)\*\*[ \t]*:+[ \t]*$/gm, "$1**");
  t = t.replace(/^(\*\*[^*]+\*\*)[ \t]*:+[ \t]+/gm, "$1 ");
  t = t.replace(/^(\*\*[^*]+):\s*\*\*/gm, "$1**");
  t = t.replace(/[ \t]+:[ \t]*$/gm, "");
  t = t.replace(/[ \t]+\n/g, "\n");
  t = t.replace(/\n{3,}/g, "\n\n");
  return t.trim();
}

function renderInline(text, keyPrefix = "i") {
  const content = String(text || "");
  const nodes = [];
  const re = /\*\*([^*]+)\*\*/g;
  let last = 0;
  let match;
  let key = 0;
  while ((match = re.exec(content)) !== null) {
    if (match.index > last) {
      nodes.push(
        <span key={`${keyPrefix}t${key++}`}>{content.slice(last, match.index)}</span>
      );
    }
    const label = match[1].replace(/\s*:+\s*$/, "").trim();
    nodes.push(
      <span
        key={`${keyPrefix}e${key++}`}
        className={`dp-em dp-em--${emphasisTone(label)}`}
      >
        {label}
      </span>
    );
    last = match.index + match[0].length;
  }
  if (last < content.length) {
    nodes.push(
      <span key={`${keyPrefix}t${key++}`}>{content.slice(last)}</span>
    );
  }
  return nodes.length ? nodes : null;
}

function classifyLine(line) {
  const trimmed = String(line || "").trim();
  if (!trimmed) return { type: "blank" };
  const numbered = trimmed.match(/^(\d+)[.)]\s+(.*)$/);
  if (numbered) {
    return { type: "numbered", n: numbered[1], content: numbered[2] };
  }
  const bullet = trimmed.match(/^[-•*]\s+(.*)$/);
  if (bullet) return { type: "bullet", content: bullet[1] };
  if (/^[SMART]\s+\S.+\s·\s+[SMART]\s/.test(trimmed)) {
    return { type: "smartline", content: trimmed };
  }
  const heading = trimmed.match(
    /^\*\*([^*]+)\*\*(?:\s*[—–-]\s*(.*))?$/
  );
  if (heading && heading[1].trim().length < 72) {
    return {
      type: "heading",
      title: heading[1].replace(/\s*:+\s*$/, "").trim(),
      rest: (heading[2] || "").replace(/^\s*:+\s*/, "").trim(),
    };
  }
  return { type: "text", content: trimmed };
}

function isListStarter(item) {
  return item?.type === "numbered" || item?.type === "bullet";
}

/**
 * Split a list-item first line into title + meta when Pingflo formats drills as
 * `**Name** — Type · 12 min` or `Name (12 min) — Type`.
 */
export function splitExerciseListContent(content) {
  const raw = String(content || "").trim();
  if (!raw) return { title: "", meta: "", looksLikeExercise: false };

  let title = raw;
  let meta = "";
  const dashSplit = raw.match(/^(.*?)(?:\s+[—–-]\s+|\s+·\s+)(.+)$/);
  if (dashSplit) {
    title = dashSplit[1].trim();
    meta = dashSplit[2].trim();
  }

  const durationMatch =
    raw.match(/\((\d+)\s*(?:min|mins|minutes?)\)/i) ||
    raw.match(/(\d+)\s*(?:min|mins|minutes?)\b/i) ||
    meta.match(/(\d+)\s*(?:min|mins|minutes?)\b/i);
  const duration = durationMatch ? `${durationMatch[1]} min` : "";

  if (duration && !meta) {
    title = title
      .replace(/\(\d+\s*(?:min|mins|minutes?)\)/gi, "")
      .replace(/\b\d+\s*(?:min|mins|minutes?)\b/gi, "")
      .replace(/\s{2,}/g, " ")
      .trim();
    meta = duration;
  } else if (duration && meta && !/\d+\s*min/i.test(meta)) {
    meta = `${meta} · ${duration}`;
  }

  const looksLikeExercise =
    Boolean(duration) ||
    /\*\*[^*]+\*\*/.test(raw) ||
    /[—–-]/.test(raw) ||
    /\bmin\b/i.test(raw);

  return { title: title || raw, meta, looksLikeExercise };
}

function collectListItems(lines, startIndex, style) {
  const items = [];
  let i = startIndex;
  while (i < lines.length) {
    const item = classifyLine(lines[i]);
    if (item.type !== style) break;
    const entry = {
      ...item,
      lines: [item.content],
    };
    i += 1;
    while (i < lines.length) {
      const next = classifyLine(lines[i]);
      if (next.type === "blank") break;
      if (isListStarter(next)) break;
      if (next.type === "heading") break;
      entry.lines.push(next.content);
      i += 1;
    }
    // Skip one blank between numbered items so "1. …\n\n2. …" stays one list.
    if (i < lines.length && classifyLine(lines[i]).type === "blank") {
      const peek = i + 1 < lines.length ? classifyLine(lines[i + 1]) : null;
      if (peek && peek.type === style) i += 1;
    }
    items.push(entry);
  }
  return { items, nextIndex: i };
}

export function parseDigipingBlocks(text) {
  const lines = normalizeDigipingText(text).split("\n");
  const blocks = [];
  let i = 0;
  while (i < lines.length) {
    const item = classifyLine(lines[i]);
    if (item.type === "blank") {
      i += 1;
      continue;
    }
    if (item.type === "bullet" || item.type === "numbered") {
      const collected = collectListItems(lines, i, item.type);
      blocks.push({
        type: "list",
        style: item.type === "numbered" ? "numbered" : "bullet",
        items: collected.items,
      });
      i = collected.nextIndex;
      continue;
    }
    blocks.push(item);
    i += 1;
  }
  return blocks;
}

function renderListItemCopy(it, keyPrefix) {
  const primary = it.lines?.[0] || it.content || "";
  const extras = (it.lines || []).slice(1);
  const { title, meta, looksLikeExercise } = splitExerciseListContent(primary);

  if (!looksLikeExercise) {
    return (
      <span className="dp-rich-li-copy">
        <span className="dp-rich-line">
          {renderInline(primary, `${keyPrefix}a`)}
        </span>
        {extras.map((line, k) => (
          <span key={`${keyPrefix}x${k}`} className="dp-rich-line dp-rich-line--sub">
            {renderInline(line, `${keyPrefix}x${k}`)}
          </span>
        ))}
      </span>
    );
  }

  return (
    <span className="dp-rich-li-copy dp-rich-li-copy--exercise">
      <span className="dp-rich-ex-title">
        {renderInline(title, `${keyPrefix}t`)}
      </span>
      {meta ? (
        <span className="dp-rich-ex-meta">
          {renderInline(meta, `${keyPrefix}m`)}
        </span>
      ) : null}
      {extras.map((line, k) => (
        <span key={`${keyPrefix}x${k}`} className="dp-rich-ex-note">
          {renderInline(line, `${keyPrefix}x${k}`)}
        </span>
      ))}
    </span>
  );
}

function renderRichNodes(text) {
  const blocks = parseDigipingBlocks(text);

  return blocks.map((block, idx) => {
    if (block.type === "heading") {
      return (
        <div key={`h${idx}`} className="dp-rich-section">
          <div className={`dp-rich-heading dp-em dp-em--${emphasisTone(block.title)}`}>
            {block.title}
          </div>
          {block.rest ? (
            <p className="dp-rich-p">{renderInline(block.rest, `h${idx}`)}</p>
          ) : null}
        </div>
      );
    }
    if (block.type === "smartline") {
      return (
        <p key={`s${idx}`} className="dp-rich-smartline">
          {block.content}
        </p>
      );
    }
    if (block.type === "list") {
      const exerciseHeavy =
        block.style === "numbered" &&
        block.items.filter((it) =>
          splitExerciseListContent(it.lines?.[0] || it.content || "").looksLikeExercise
        ).length >= Math.max(1, Math.ceil(block.items.length * 0.5));
      return (
        <ul
          key={`l${idx}`}
          className={`${
            block.style === "numbered" ? "dp-rich-ol" : "dp-rich-ul"
          }${exerciseHeavy ? " dp-rich-ol--session-plan" : ""}`}
        >
          {block.items.map((it, j) => (
            <li
              key={`li${idx}-${j}`}
              className={`dp-rich-li${
                exerciseHeavy ? " dp-rich-li--exercise" : ""
              }`}
            >
              {block.style === "numbered" ? (
                <span className="dp-rich-index" aria-hidden="true">
                  {it.n}
                </span>
              ) : (
                <span className="dp-rich-dot" aria-hidden="true" />
              )}
              {renderListItemCopy(it, `l${idx}${j}`)}
            </li>
          ))}
        </ul>
      );
    }
    return (
      <p key={`p${idx}`} className="dp-rich-p">
        {renderInline(block.content, `p${idx}`)}
      </p>
    );
  });
}

export function RichText({ text, className = "" }) {
  return (
    <div className={`dp-rich-text ${className}`.trim()}>
      {renderRichNodes(text)}
    </div>
  );
}

export function visiblePlainSlice(text, count) {
  const slice = normalizeDigipingText(text).slice(0, count);
  return slice
    .replace(/\*\*([^*]*)\*\*/g, "$1")
    .replace(/\*\*[^*]*$/, (m) => m.replace(/\*\*/g, ""))
    .replace(/^\*\s+/gm, "")
    .replace(/^[-•]\s+/gm, "")
    .replace(/^[ \t]*:+[ \t]*/gm, "");
}

export const __test__ = {
  classifyLine,
  collectListItems,
  emphasisTone,
};
